/**
 * Past meetings on a series' Meetings tab (#302), and past one-off meetings
 * (#308): the last few as cards with what came out of them, the rest of this
 * year as one compact table grouped by month, and earlier years collapsed to a
 * line each.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { formatRelativeDate } from '../../lib/relativeTime';

export type MeetingItemKind = 'task' | 'note' | 'follow-up' | 'excerpt';

/** Something that came out of one meeting, ready to list under it. */
export interface MeetingItem {
  kind: MeetingItemKind;
  id: string;
  title: string;
  done: boolean;
  to: string;
}

export interface PastMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
}

export interface MonthGroup<M> { key: string; label: string; meetings: M[] }
export interface YearGroup<M> { year: string; months: MonthGroup<M>[]; count: number }

/** How many past meetings keep a full card, how many months start open, how many items a card lists before "+N more". */
export const RECENT_COUNT = 3;
export const OPEN_MONTHS = 2;
export const CARD_ITEMS = 2;

const KIND_ORDER: MeetingItemKind[] = ['task', 'note', 'follow-up', 'excerpt'];
/** Each kind's colour, the same as its tab's. */
export const KIND_TEXT: Record<MeetingItemKind, string> = {
  task: 'text-excerpt-task', note: 'text-excerpt-note', 'follow-up': 'text-follow-up', excerpt: 'text-excerpt',
};
const KIND_DOT: Record<MeetingItemKind, string> = {
  task: 'bg-excerpt-task', note: 'bg-excerpt-note', 'follow-up': 'bg-follow-up', excerpt: 'bg-excerpt',
};

const MONTH_KEY = /^(\d{4})-(\d{2})/;
const OTHER = 'Other dates';

function monthLabel(key: string): string {
  const d = new Date(`${key}-01T00:00:00`);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString('en-US', { month: 'long' });
}

/** Month groups, newest first. Labels carry no year: every list of them sits under a heading that names it. */
function byMonth<M extends PastMeeting>(meetings: M[]): MonthGroup<M>[] {
  const groups = new Map<string, M[]>();
  for (const m of meetings) {
    const key = MONTH_KEY.exec(m.meeting_date)?.[0] ?? OTHER;
    groups.set(key, [...(groups.get(key) ?? []), m]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === OTHER ? 1 : b === OTHER ? -1 : b.localeCompare(a)))
    .map(([key, ms]) => ({ key, label: key === OTHER ? OTHER : monthLabel(key), meetings: ms }));
}

/**
 * Splits past meetings (newest first) into the recent few, this year's months
 * and earlier years. A date that is not YYYY-MM-... lands in "Other dates" at
 * the end rather than breaking the grouping.
 */
export function groupPastMeetings<M extends PastMeeting>(pastNewestFirst: M[], today: string) {
  const recent = pastNewestFirst.slice(0, RECENT_COUNT);
  const rest = pastNewestFirst.slice(RECENT_COUNT);
  const thisYear = today.slice(0, 4);
  const yearOf = (m: M) => MONTH_KEY.exec(m.meeting_date)?.[1] ?? OTHER;
  const months = byMonth(rest.filter((m) => yearOf(m) === thisYear));
  const years = new Map<string, M[]>();
  for (const m of rest) {
    if (yearOf(m) !== thisYear) years.set(yearOf(m), [...(years.get(yearOf(m)) ?? []), m]);
  }
  const yearGroups: YearGroup<M>[] = [...years.entries()]
    .sort(([a], [b]) => (a === OTHER ? 1 : b === OTHER ? -1 : b.localeCompare(a)))
    .map(([year, ms]) => ({ year, months: byMonth(ms), count: ms.length }));
  return { recent, months, years: yearGroups };
}

/** "2 tasks", "1 follow-up" ... in a fixed order, only for kinds that are present. */
export function itemCounts(items: MeetingItem[]): { kind: MeetingItemKind; text: string }[] {
  return KIND_ORDER.flatMap((kind) => {
    const n = items.filter((i) => i.kind === kind).length;
    return n ? [{ kind, text: `${n} ${kind}${n === 1 ? '' : 's'}` }] : [];
  });
}

/** The words in a stored body: ProseMirror JSON, or legacy HTML / plain text. */
export function bodyText(body: string | null | undefined): string {
  if (!body) return '';
  const words: string[] = [];
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return;
    const n = node as { text?: unknown; content?: unknown };
    if (typeof n.text === 'string') words.push(n.text);
    if (Array.isArray(n.content)) n.content.forEach(walk);
  };
  try {
    walk(JSON.parse(body));
  } catch {
    words.push(body.replace(/<[^>]*>/g, ' '));
  }
  return words.join(' ').replace(/\s+/g, ' ').trim();
}

/** Notes count as written when the body has words that are not just the series template. */
export function hasWrittenNotes(body: string | null | undefined, template: string | null | undefined): boolean {
  const text = bodyText(body);
  return text !== '' && text !== bodyText(template);
}

function shortDate(d: string): string {
  const date = new Date(`${d.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? d : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Relative time only while it says more than the date beside it does. */
function recentRelative(d: string): string | null {
  const text = formatRelativeDate(d);
  return /ago$|^Yesterday$|^Today$/.test(text) ? text : null;
}

function Counts({ items }: { items: MeetingItem[] }) {
  const counts = itemCounts(items);
  if (counts.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-x-3 text-xs">
      {counts.map((c) => <span key={c.kind} className={KIND_TEXT[c.kind]}>{c.text}</span>)}
    </span>
  );
}

function NotesTag({ show }: { show: boolean }) {
  if (!show) return null;
  return <span className="shrink-0 rounded border border-border-hover px-1.5 text-[11px] text-text-secondary">notes</span>;
}

function ItemLine({ item }: { item: MeetingItem }) {
  return (
    <li className="flex items-baseline gap-2 text-sm" data-testid="meeting-item">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 translate-y-[-1px] rounded-sm ${KIND_DOT[item.kind]}`} />
      <span className={`w-16 shrink-0 text-[11px] ${KIND_TEXT[item.kind]}`}>{item.kind}</span>
      <Link
        to={item.to}
        className={`min-w-0 hover:text-accent [overflow-wrap:anywhere] ${item.done ? 'text-text-muted line-through' : 'text-text-primary'}`}
      >
        {item.title || '(untitled)'}
      </Link>
    </li>
  );
}

function Chevron({ open }: { open: boolean }) {
  return <ChevronRight size={14} aria-hidden="true" className={`shrink-0 text-text-muted transition-transform ${open ? 'rotate-90' : ''}`} />;
}

interface Props<M extends PastMeeting> {
  meetingHref: (id: string) => string;
  /** Per-meeting controls, such as delete, shown at the end of its card or row. */
  actions?: (m: M) => ReactNode;
  /** Start with every month and year open, as when the list is filtered. */
  expandAll?: boolean;
  meetings: M[];
  today: string;
  itemsByMeeting: Map<string, MeetingItem[]>;
  withNotes: Set<string>;
  formatWhen: (m: M) => { heading: string; sub: string | null; when: string | null };
}

export function PastMeetings<M extends PastMeeting>({ meetingHref, actions, expandAll, meetings, today, itemsByMeeting, withNotes, formatWhen }: Props<M>) {
  const { recent, months, years } = groupPastMeetings(meetings, today);
  const [openMonths, setOpenMonths] = useState(() => new Set(expandAll
    ? [...months.map((g) => g.key), ...years.flatMap((y) => [`year-${y.year}`, ...y.months.map((g) => g.key)])]
    : months.slice(0, OPEN_MONTHS).map((g) => g.key)));
  const [openRows, setOpenRows] = useState(() => new Set<string>());
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    setter(next);
  };
  const itemsOf = (m: M) => itemsByMeeting.get(m.id) ?? [];
  const isEmpty = (m: M) => itemsOf(m).length === 0 && !withNotes.has(m.id);

  const card = (m: M) => {
    const items = itemsOf(m);
    const open = openRows.has(m.id);
    const shown = open ? items : items.slice(0, CARD_ITEMS);
    const { heading, sub } = formatWhen(m);
    const rel = recentRelative(m.meeting_date);
    return (
      <div key={m.id} data-testid="past-meeting" data-meeting-id={m.id}
        className={`rounded-lg border border-border bg-bg-card px-4 py-3 ${isEmpty(m) ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <Link to={meetingHref(m.id)} className="group min-w-0">
            <h3 className="text-sm font-medium text-text-primary group-hover:text-accent [overflow-wrap:anywhere]">{heading}</h3>
            {sub && <p className="mt-0.5 text-xs text-text-muted">{sub}</p>}
          </Link>
          <div className="flex shrink-0 items-center gap-2">
            <NotesTag show={withNotes.has(m.id)} />
            {rel && <span className="text-xs text-text-muted">{rel}</span>}
            {actions?.(m)}
          </div>
        </div>
        {items.length > 0 && (
          <ul className="mt-2 space-y-1 border-t border-border pt-2">
            {shown.map((i) => <ItemLine key={`${i.kind}-${i.id}`} item={i} />)}
          </ul>
        )}
        {items.length > CARD_ITEMS && (
          <button type="button" onClick={() => toggle(openRows, setOpenRows, m.id)} aria-expanded={open}
            className="mt-1 pl-4 text-xs text-text-secondary hover:text-text-primary">
            {open ? 'Show less' : `+ ${items.length - CARD_ITEMS} more`}
          </button>
        )}
      </div>
    );
  };

  const row = (m: M) => {
    const items = itemsOf(m);
    const open = openRows.has(m.id);
    const date = shortDate(m.meeting_date);
    const { when } = formatWhen(m);
    return (
      <div key={m.id} data-testid="past-meeting" data-meeting-id={m.id} className={isEmpty(m) ? 'opacity-60' : ''}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 pl-2 pr-3">
          {items.length > 0 ? (
            <button type="button" onClick={() => toggle(openRows, setOpenRows, m.id)} aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} items from ${date}`} className="rounded p-1 hover:bg-bg-hover">
              <Chevron open={open} />
            </button>
          ) : <span className="w-[22px] shrink-0" />}
          <Link to={meetingHref(m.id)} className="group flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3">
            <span className="w-28 shrink-0 text-sm font-medium text-text-primary group-hover:text-accent">{date}</span>
            {when && <span className="text-xs text-text-muted">{when}</span>}
            {m.title && <span className="min-w-0 truncate text-xs text-text-secondary">{m.title}</span>}
          </Link>
          <NotesTag show={withNotes.has(m.id)} />
          <Counts items={items} />
          {actions?.(m)}
        </div>
        {open && (
          <ul className="mb-2 ml-9 mr-3 space-y-1 rounded-md bg-bg-surface px-3 py-2">
            {items.map((i) => <ItemLine key={`${i.kind}-${i.id}`} item={i} />)}
          </ul>
        )}
      </div>
    );
  };

  const monthBlock = (g: MonthGroup<M>) => {
    const open = openMonths.has(g.key);
    const counts = itemCounts(g.meetings.flatMap(itemsOf));
    return (
      <div key={g.key} data-testid="past-month">
        <button type="button" onClick={() => toggle(openMonths, setOpenMonths, g.key)} aria-expanded={open}
          className="flex w-full flex-wrap items-center gap-x-3 gap-y-0.5 bg-bg-card px-3 py-2 text-left hover:bg-bg-hover">
          <Chevron open={open} />
          <span className="w-28 text-sm font-semibold text-text-primary">{g.label}</span>
          <span className="text-xs text-text-muted">
            {g.meetings.length} {g.meetings.length === 1 ? 'meeting' : 'meetings'}
            {counts.map((c) => <span key={c.kind}>{', '}<span className={KIND_TEXT[c.kind]}>{c.text}</span></span>)}
          </span>
        </button>
        {open && <div className="divide-y divide-border/60">{g.meetings.map(row)}</div>}
      </div>
    );
  };

  const heading = (text: string) => <h2 className="pt-2 text-xs font-semibold uppercase tracking-wide text-text-muted">{text}</h2>;

  return (
    <section aria-label="Past meetings" className="space-y-2">
      {heading('Recent')}
      {recent.map(card)}
      {months.length > 0 && (
        <>
          {heading(`Earlier in ${today.slice(0, 4)}`)}
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">{months.map(monthBlock)}</div>
        </>
      )}
      {years.length > 0 && (
        <>
          {heading('Earlier years')}
          {years.map((y) => {
            const key = `year-${y.year}`;
            const open = openMonths.has(key);
            const counts = itemCounts(y.months.flatMap((g) => g.meetings.flatMap(itemsOf)));
            return (
              <div key={key} data-testid="past-year" className="overflow-hidden rounded-lg border border-border">
                <button type="button" onClick={() => toggle(openMonths, setOpenMonths, key)} aria-expanded={open}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-left hover:bg-bg-hover">
                  <Chevron open={open} />
                  <span className="w-28 text-sm font-semibold text-text-primary">{y.year}</span>
                  <span className="text-xs text-text-muted">
                    {y.count} {y.count === 1 ? 'meeting' : 'meetings'}
                    {counts.map((c) => <span key={c.kind}>{', '}<span className={KIND_TEXT[c.kind]}>{c.text}</span></span>)}
                  </span>
                </button>
                {open && <div className="divide-y divide-border border-t border-border">{y.months.map(monthBlock)}</div>}
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

/** Everything that came out of each meeting, keyed by meeting id. Follow-ups are tasks too; each is listed once, as a follow-up. */
export function buildMeetingItems(input: {
  meetingHref: (id: string) => string;
  tasks: { id: string; title: string; meeting_id: string | null; status?: string }[];
  notes: { id: string; title: string; meeting_id: string | null }[];
  followUps: { id: string; title: string; status: string; source_meeting_id?: string | null }[];
  excerpts: { id: string; plain_text: string | null; source: { id: string }; made_here?: boolean }[];
}): Map<string, MeetingItem[]> {
  const out = new Map<string, MeetingItem[]>();
  const seen = new Set<string>();
  const add = (meetingId: string | null | undefined, item: MeetingItem) => {
    if (!meetingId || seen.has(`${item.kind === 'follow-up' ? 'task' : item.kind}:${item.id}`)) return;
    seen.add(`${item.kind === 'follow-up' ? 'task' : item.kind}:${item.id}`);
    out.set(meetingId, [...(out.get(meetingId) ?? []), item]);
  };
  const isDone = (s?: string) => s === 'done' || s === 'cancelled';
  const followIds = new Set(input.followUps.map((f) => f.id));
  for (const t of input.tasks) {
    add(t.meeting_id, { kind: followIds.has(t.id) ? 'follow-up' : 'task', id: t.id, title: t.title, done: isDone(t.status), to: `/tasks/${t.id}` });
  }
  for (const f of input.followUps) {
    add(f.source_meeting_id, { kind: 'follow-up', id: f.id, title: f.title, done: isDone(f.status), to: `/tasks/${f.id}` });
  }
  for (const n of input.notes) add(n.meeting_id, { kind: 'note', id: n.id, title: n.title, done: false, to: `/notes/${n.id}` });
  for (const e of input.excerpts) {
    if (!e.made_here) continue;
    const text = (e.plain_text ?? '').replace(/\s+/g, ' ').trim();
    const title = text.length > 80 ? `${text.slice(0, 79)}...` : text;
    add(e.source.id, { kind: 'excerpt', id: e.id, title: title ? `"${title}"` : '', done: false, to: input.meetingHref(e.source.id) });
  }
  for (const list of out.values()) list.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
  return out;
}
